/**
 * "Found 14 connections in DBeaver, SSMS and 2 more apps" — the start-page
 * card that puts connection import in front of the people who need it. The
 * Import… menu used to live only in Manage Connections, so import sources were
 * detected in ~15x more sessions than the menu was ever opened; this card is
 * that offer, made where first-run users actually land.
 *
 * Dismissible, and the dismissal sticks (see `importDiscoveryStore`) — the
 * menus in Manage Connections, the New Connection dialog and the command
 * palette remain the permanent home of the flow.
 */
import { useEffect } from 'react';
import { PackageOpen, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { ImportSourceId } from '../../../types/connectionImport';
import { ImportSourcesMenu } from './ImportSourcesMenu';
import { openImportSource } from './connectionImportService';
import { useImportDetection } from './useImportDetection';
import { useImportDiscoveryStore } from './importDiscoveryStore';
import {
  buildImportDiscoveryHeadline,
  pickCardSources,
  shortSourceLabel,
  shouldShowImportDiscoveryCard,
} from './importDiscoveryCardRules';

/** One `feature:import_card_shown` per app session, however often the start page remounts. */
let trackedShownThisSession = false;

export function ImportDiscoveryCard() {
  const { track } = useAnalytics();
  const dismissed = useImportDiscoveryStore((state) => state.dismissed);
  const dismiss = useImportDiscoveryStore((state) => state.dismiss);
  // Detection still runs when dismissed (it is what `feature:import_source_detected`
  // measures, and it is only existsSync probes) — but counting is a real scan
  // per source, and nobody should pay for it every session to render nothing.
  const { detectedSources, counts, countsLoaded, totalCount } = useImportDetection({
    withCounts: !dismissed,
  });

  const sourceCount = detectedSources.length;
  const show = shouldShowImportDiscoveryCard({
    detectedCount: sourceCount,
    dismissed,
    countsLoaded,
    totalCount,
  });

  useEffect(() => {
    if (!show || trackedShownThisSession) return;
    trackedShownThisSession = true;
    track('feature:import_card_shown', { source_count: sourceCount });
  }, [show, sourceCount, track]);

  if (!show) return null;

  const handleDismiss = () => {
    track('feature:import_card_dismissed', { source_count: sourceCount });
    dismiss();
  };

  // Direct one-click buttons for the biggest detected stores; everything else
  // stays reachable through the shared Import… menu ("More sources").
  const cardSources = pickCardSources(detectedSources, counts, countsLoaded);
  const handleSourceClick = (sourceId: ImportSourceId) => {
    track('feature:import_card_click', { source: sourceId });
    openImportSource(sourceId, 'empty_state');
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-md border border-primary/40 bg-primary/5 px-4 py-3 @xl:flex-row @xl:items-center"
      data-testid="import-discovery-card"
    >
      <PackageOpen className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-pretty" data-testid="import-discovery-card-headline">
          {buildImportDiscoveryHeadline(detectedSources, totalCount)}
        </p>
        <p className="text-xs mt-0.5 text-muted-foreground text-pretty">
          Bring them across in a couple of clicks — you review every one first, and nothing connects
          on its own.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1 shrink-0">
        {cardSources.map((source, index) => {
          const count = counts[source.id];
          return (
            <Button
              key={source.id}
              type="button"
              variant={index === 0 ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSourceClick(source.id)}
              data-testid={`import-discovery-card-source-${source.id}`}
              className="active:scale-[0.96] transition-[color,box-shadow,scale]"
            >
              {index === 0 && <PackageOpen className="h-4 w-4" aria-hidden="true" />}
              {index === 0 ? `Import from ${shortSourceLabel(source.label)}` : shortSourceLabel(source.label)}
              {typeof count === 'number' && count > 0 && (
                <span className="tabular-nums opacity-75">({count})</span>
              )}
            </Button>
          );
        })}
        <ImportSourcesMenu
          entry="empty_state"
          label={cardSources.length > 0 ? 'More' : 'Import connections'}
          variant="ghost"
          testId="import-discovery-card-button"
          counts={counts}
        />
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground"
          onClick={handleDismiss}
          aria-label="Dismiss import suggestion"
          data-testid="import-discovery-card-dismiss"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
